/**
 * LPP-22962 (RDT-LPP-22291 §3 SCFE-1): modello e validazione dei due box extra costi manuali al
 * check-in — lavaggio (negligenza) e recupero vettura — col modello "field carburante".
 *
 * Importo inserito a mano dall'operatore (nessun pricing PRC) e allegato OBBLIGATORIO per il
 * salvataggio quando l'importo è valorizzato (AS-09). L'allegato è mostrato nella sezione allegati.
 */

export type ExtraCostKind = 'wash' | 'recovery';

export interface ExtraCostDocument {
    documentFile: string;
    path: string;
    contentType?: string;
    documentTitle: string;
    fileSize: number;
}

export type ExtraCostAttachment = File | ExtraCostDocument;

export interface ExtraCostItem {
    /** Importo manuale in centesimi (>= 0). Stringa vuota = non valorizzato. */
    amount: number | '';
    description: string;
    /** Allegato probatorio (foto/documento). Obbligatorio se amount > 0. */
    attachment: ExtraCostAttachment | null;
}

export interface ExtraCostsValue {
    wash: ExtraCostItem;
    recovery: ExtraCostItem;
}

export const EMPTY_EXTRA_COST_ITEM: ExtraCostItem = {
    amount: '',
    description: '',
    attachment: null,
};

export const EMPTY_EXTRA_COSTS: ExtraCostsValue = {
    wash: { ...EMPTY_EXTRA_COST_ITEM },
    recovery: { ...EMPTY_EXTRA_COST_ITEM },
};

/** true se l'importo è valorizzato (> 0). Valori vuoti, zero o negativi = non valorizzato. */
export function hasAmount(item: ExtraCostItem): boolean {
    return typeof item.amount === 'number' && item.amount > 0;
}

/** true se l'allegato è presente. */
export function hasAttachment(item: ExtraCostItem): boolean {
    return item.attachment != null;
}

export function isExtraCostDocument(
    attachment: ExtraCostAttachment | null
): attachment is ExtraCostDocument {
    return attachment != null && 'documentFile' in attachment;
}

export function euroInputToCents(value: unknown): number | '' {
    const cents = Math.round(parseFloat(String(value ?? '0').replace(',', '.')) * 100);
    return Number.isFinite(cents) && cents > 0 ? cents : '';
}

export function centsToEuroInput(value: number | ''): string {
    return typeof value === 'number' && value > 0 ? (value / 100).toString() : '0';
}

/**
 * Un singolo box è valido se: non è valorizzato, oppure è valorizzato e ha l'allegato (AS-09).
 * Un box con allegato ma senza importo è considerato non valorizzato (nessun addebito) → valido.
 */
export function isItemValid(item: ExtraCostItem): boolean {
    return !hasAmount(item) || hasAttachment(item);
}

/** L'intero blocco è valido se entrambi i box sono validi. */
export function validateExtraCosts(value: ExtraCostsValue): boolean {
    return isItemValid(value.wash) && isItemValid(value.recovery);
}

/** Elenca le tipologie di extra valorizzate ma prive di allegato (per messaggi di errore). */
export function missingAttachmentKinds(value: ExtraCostsValue): ExtraCostKind[] {
    const kinds: ExtraCostKind[] = [];
    if (hasAmount(value.wash) && !hasAttachment(value.wash)) {
        kinds.push('wash');
    }
    if (hasAmount(value.recovery) && !hasAttachment(value.recovery)) {
        kinds.push('recovery');
    }
    return kinds;
}
